import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateTeamRequest,
  CreateTeamResponse,
  DeleteTeamRequest,
  DeleteTeamResponse,
  UpdateTeamRequest,
  UpdateTeamResponse,
  ViewTeamRequest,
  ViewTeamResponse
} from './team.types';

import { CREATING_TEAM, DELETING_TEAM, RETRIEVING_TEAM, UPDATING_TEAM } from './team.constants';

import { requestCreateTeam, requestDeleteTeam, requestUpdateTeam, requestViewTeam } from './team.actions';

import { getCreateTeamRequest, getDeleteTeamRequest, getUpdateTeamRequest, getViewTeamRequest } from './team.endpoints';

import { addTeam, changeTeamName, removeTeam, setTeam } from '../../slices/programme/team/team.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateTeamRequest({ payload }: SagaPayload<CreateTeamRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_TEAM));

    const request = getCreateTeamRequest(requestData);

    const { data }: AxiosResponse<CreateTeamResponse> = yield call(axios, ...request);

    yield put(addTeam(data.team));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_TEAM));
  }
}

export function* watchForCreateTeamRequest() {
  yield takeLatest(requestCreateTeam.type, performCreateTeamRequest);
}

export function* performViewTeamRequest({ payload }: SagaPayload<ViewTeamRequest>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TEAM));

    const request = getViewTeamRequest(requestData);

    const { data }: AxiosResponse<ViewTeamResponse> = yield call(axios, ...request);

    yield put(setTeam(data.team));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TEAM));
  }
}

export function* watchForViewTeamRequest() {
  yield takeLatest(requestViewTeam.type, performViewTeamRequest);
}

export function* performUpdateTeamRequest({ payload }: SagaPayload<UpdateTeamRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_TEAM));

    const request = getUpdateTeamRequest(requestData);

    const { data }: AxiosResponse<UpdateTeamResponse> = yield call(axios, ...request);

    yield put(changeTeamName(data.team));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_TEAM));
  }
}

export function* watchForUpdateTeamRequest() {
  yield takeLatest(requestUpdateTeam.type, performUpdateTeamRequest);
}

export function* performDeleteTeamRequest({ payload }: SagaPayload<DeleteTeamRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_TEAM));

    const request = getDeleteTeamRequest(requestData);

    const { data }: AxiosResponse<DeleteTeamResponse> = yield call(axios, ...request);

    yield put(removeTeam(requestData.teamExternalReference));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_TEAM));
  }
}

export function* watchForDeleteTeamRequest() {
  yield takeLatest(requestDeleteTeam.type, performDeleteTeamRequest);
}

export default function* teamSaga() {
  yield all([
    watchForCreateTeamRequest(),
    watchForViewTeamRequest(),
    watchForUpdateTeamRequest(),
    watchForDeleteTeamRequest()
  ]);
}
